import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import SelectProceso from "./selectProceso";
import ComunicacionesApi from "api/comunicacionesApi";
import useComunicacionEditorImages from "../hooks/useComunicacionEditorImages";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
`;

const CreateComunicacion = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    proceso: "",
    audiencia: "",
    destinatarios: [],
    tipo_comunicacion: "",
    formato_comunicacion: "notificacionManual",
    asunto: "",
    contenido: "",
  });
  const [errors, setErrors] = useState({});
  const quillRef = useRef(null);
  const [contenido, setContenido] = useState("");
  const [showProcesoModal, setShowProcesoModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);

  // Hook para manejo de imágenes
  const {
    uploadingImage,
    imageError,
    tempImages,
    setupImageHandler,
    processTempImages,
    //cleanOrphanImages,
    getQuillModules,
    getQuillFormats,
    clearTempImages,
    clearError,
  } = useComunicacionEditorImages({
    procesoIdentificador: formData.proceso?.identificador || null,
    comunicacionId: null,
  });

  // Configurar el handler de imágenes cuando el editor esté listo
  useEffect(() => {
    if (quillRef.current && formData.proceso?.identificador) {
      setupImageHandler(quillRef, "contenido");
    }
  }, [setupImageHandler, formData.proceso]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    if (field === "asunto") {
      const cleanValue = value.replace(/[^a-záéíóúñüA-ZÁÉÍÓÚÑÜ0-9\s]/g, "").toUpperCase();
      setFormData((prev) => ({ ...prev, [field]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleEditorContenido = useCallback((value) => {
    setContenido(value);
  }, []);

  const handleDestinatarioChange = (index, field) => (event) => {
    const value = event.target.value;
    const updatedDestinatarios = [...formData.destinatarios];
    updatedDestinatarios[index] = { ...updatedDestinatarios[index], [field]: value };
    setFormData((prev) => ({ ...prev, destinatarios: updatedDestinatarios }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateEmailInput = (value) => {
    let cleanValue = value.trim();
    cleanValue = cleanValue.toLowerCase();
    cleanValue = cleanValue.replace(/[^a-z0-9@._-]/g, "");
    return {
      value: cleanValue,
      isValid: isValidEmail(cleanValue),
      errorMessage: isValidEmail(cleanValue) ? "" : "Por favor ingrese un Email válido",
    };
  };

  const handleEmailChange = (index, value) => {
    const result = validateEmailInput(value);
    const email = result.value;
    const updatedDestinatarios = [...formData.destinatarios];
    updatedDestinatarios[index] = { ...updatedDestinatarios[index], email: email };
    setFormData((prev) => ({ ...prev, destinatarios: updatedDestinatarios }));
  };

  const addDestinatario = () => {
    setFormData((prev) => ({
      ...prev,
      destinatarios: [...prev.destinatarios, { nombre_completo: "" }],
    }));
  };

  const removeDestinatario = (index) => {
    const updatedDestinatarios = formData.destinatarios.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      destinatarios: updatedDestinatarios.map((destinatario) => ({ ...destinatario })),
    }));
  };

  const handleProcesoSelect = (selectedProceso) => {
    setFormData((prev) => ({
      ...prev,
      proceso: selectedProceso,
    }));
    setShowProcesoModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Limpiar errores previos
    setErrors({});
    clearError();
    // Validaciones básicas
    if (!formData.asunto || formData.asunto.length < 5) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral:
          "El Asunto es un campo obligatorio y debe superar una longitud de 5 caracteres.",
      }));
      return;
    }
    if (!formData.proceso) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "Debe seleccionar un Proceso.",
      }));
      return;
    }
    if (!formData.tipo_comunicacion || formData.tipo_comunicacion === "") {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "El Tipo de Comunicación es un campo obligatorio.",
      }));
      return;
    }
    const parser = new DOMParser();
    const docContenido = parser.parseFromString(contenido, "text/html");
    const htmlLimpioContenido = docContenido.body.innerHTML;
    const longitudMinima = 50;
    if (!htmlLimpioContenido || htmlLimpioContenido.length < longitudMinima) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: `El Contenido es un campo obligatorio y debe superar una longitud de ${longitudMinima} caracteres.`,
      }));
      return;
    }

    if (formData.destinatarios.length < 1) {
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "Debe ingresar al menos un Destinatarios.",
      }));
      return;
    }

    const confirmAction = window.confirm(`¿Estás seguro(a) que deseas crear la Comunicación?`);
    if (!confirmAction) return;

    try {
      setSaving(true);

      // 1. Crear la comunicación inicialmente (sin procesar imágenes)
      const dataToSend = {
        ...formData,
        contenido: htmlLimpioContenido,
        formato_comunicacion: "notificacionManual",
        destinatarios: formData.destinatarios.map((dest) => ({
          ...dest,
          notificado: false,
          pdf_url: null,
          error_envio: null,
        })),
      };

      console.log("Creando comunicación inicial...");
      const response = await ComunicacionesApi.crearComunicacion(formData.proceso.id, dataToSend);

      if (!response.data || !response.data.id) {
        throw new Error("No se pudo obtener el ID de la comunicación creada");
      }

      const comunicacionId = response.data.id;
      console.log("Comunicación creada con ID:", comunicacionId);

      // 2. Si hay imágenes temporales, procesarlas
      if (tempImages.length > 0) {
        console.log("Procesando imágenes temporales...");
        setProcessingImages(true);

        try {
          // Procesar las imágenes temporales y obtener el contenido actualizado
          const contenidoConImagenesFinales = await processTempImages(
            comunicacionId,
            htmlLimpioContenido
          );

          // 3. Actualizar la comunicación con el contenido final
          console.log("Actualizando comunicación con contenido final...");
          const updateData = {
            contenido: contenidoConImagenesFinales,
          };

          await ComunicacionesApi.actualizarComunicacion(comunicacionId, updateData);
          console.log("Comunicación actualizada con imágenes procesadas");
        } catch (imageError) {
          console.error("Error procesando imágenes:", imageError);
          // Aunque falle el procesamiento de imágenes, la comunicación ya fue creada
          // Mostrar advertencia pero continuar
          alert(
            obtenerMensajeError(
              imageError,
              "La comunicación fue creada pero hubo problemas procesando las imágenes. Por favor, edita la comunicación para corregir las imágenes."
            )
          );
        } finally {
          setProcessingImages(false);
        }
      }

      console.log("Comunicación creada exitosamente");
      onSuccess();
      hCClose();
    } catch (error) {
      console.error("Error al guardar comunicación:", error);
      setErrors((prev) => ({
        ...prev,
        errorGeneral: "Error al crear la comunicación. Por favor, intenta nuevamente.",
      }));
    } finally {
      setSaving(false);
    }
  };

  const resetState = () => {
    setErrors({});
    setContenido("");
    clearTempImages(); // Limpiar imágenes temporales
    setFormData({
      proceso: "",
      audiencia: "",
      destinatarios: [],
      tipo_comunicacion: "",
      formato_comunicacion: "",
      asunto: "",
      contenido: "",
    });
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  // Obtener módulos y formatos de Quill del hook
  const modules = getQuillModules();
  const formats = getQuillFormats();

  return (
    <>
      {(saving || processingImages) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
          <SuiBox ml={2}>
            <SuiTypography variant="h6" color="white">
              {saving && !processingImages && "Creando comunicación..."}
              {processingImages && "Procesando imágenes..."}
            </SuiTypography>
          </SuiBox>
        </Backdrop>
      )}

      <Dialog open={open} onClose={hCClose} maxWidth="lg" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiTypography variant="h3" fontWeight="dark">
                  Nueva Comunicación
                </SuiTypography>
              </Grid>
            </Grid>

            {errors.errorGeneral && (
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={12}>
                  <Alert severity="error" sx={{ marginBottom: "16px" }}>
                    {errors.errorGeneral}
                  </Alert>
                </Grid>
              </Grid>
            )}

            {imageError && (
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={12}>
                  <Alert severity="warning" sx={{ marginBottom: "16px" }}>
                    {imageError}
                  </Alert>
                </Grid>
              </Grid>
            )}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Deudor Proceso
                </SuiTypography>
                <SuiButton
                  variant="gradient"
                  buttonColor="info"
                  fullWidth
                  onClick={() => setShowProcesoModal(true)}
                >
                  {formData.proceso
                    ? `${formData.proceso.cliente_nombre} - ${formData.proceso.identificador}`
                    : "Seleccionar Proceso"}
                </SuiButton>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={8}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Asunto
                </SuiTypography>
                <SuiInput
                  value={formData.asunto || ""}
                  onChange={handleChange("asunto")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Tipo Comunicación
                </SuiTypography>
                <Select
                  value={formData.tipo_comunicacion}
                  onChange={handleChange("tipo_comunicacion")}
                  fullWidth
                >
                  <MenuItem value="">Seleccione Tipo Comunicación</MenuItem>
                  <MenuItem value="EMAIL">EMAIL</MenuItem>
                  <MenuItem value="FISICA">FISICA</MenuItem>
                </Select>
              </Grid>
            </Grid>

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <SuiTypography variant="h6" fontWeight="dark">
                    Contenido
                  </SuiTypography>
                  {uploadingImage && (
                    <SuiBox display="flex" alignItems="center">
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <SuiTypography variant="caption" color="text">
                        Subiendo imagen...
                      </SuiTypography>
                    </SuiBox>
                  )}
                  {tempImages?.length > 0 && (
                    <SuiTypography variant="caption" color="warning">
                      {tempImages.length} imagen(es) temporal(es) - se procesarán al crear la
                      comunicación
                    </SuiTypography>
                  )}
                </SuiBox>
                <StyledEditor
                  ref={quillRef}
                  value={contenido}
                  onChange={handleEditorContenido}
                  modules={modules}
                  formats={formats}
                />
              </Grid>
            </Grid>

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}></Grid>
            </Grid>

            {/* Sección de Destinatarios */}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox display="flex" alignItems="left" sx={{ mb: 1, mt: 1 }}>
                  <SuiTypography variant="h6" fontWeight="dark" sx={{ mr: 3 }}>
                    Destinatarios
                  </SuiTypography>
                  <SuiButton
                    variant="gradient"
                    buttonColor="dark"
                    size="small"
                    onClick={addDestinatario}
                  >
                    <AddIcon />
                  </SuiButton>
                </SuiBox>
              </Grid>
            </Grid>

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                {formData.destinatarios.map((destinatario, index) => (
                  <SuiBox key={index} alignItems="center" mb={2}>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                      <Grid size={4}>
                        <SuiBox display="flex" alignItems="left">
                          <IconButton onClick={() => removeDestinatario(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                          <SuiInput
                            value={destinatario.nombre_completo}
                            onChange={handleDestinatarioChange(index, "nombre_completo")}
                            placeholder="Nombre Completo"
                            fullWidth
                          />
                        </SuiBox>
                      </Grid>
                      <Grid size={4}>
                        <Select
                          value={destinatario.tipo_identificacion}
                          onChange={handleDestinatarioChange(index, "tipo_identificacion")}
                          placeholder="Tipo de Identificación"
                          fullWidth
                        >
                          <MenuItem value="">Seleccione Tipo de Identificación</MenuItem>
                          <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                          <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                          <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                          <MenuItem value="PAS">Pasaporte</MenuItem>
                          <MenuItem value="OTRO">Otro</MenuItem>
                        </Select>
                      </Grid>
                      <Grid size={4}>
                        <SuiInput
                          value={destinatario.identificacion}
                          onChange={handleDestinatarioChange(index, "identificacion")}
                          placeholder="Identificación, max. 20 dígitos."
                        />
                      </Grid>
                    </Grid>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                      <Grid size={3}>
                        <SuiInput
                          value={destinatario.tipo}
                          onChange={handleDestinatarioChange(index, "tipo")}
                          placeholder="Tipo Destinatario"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={3}>
                        <SuiInput
                          value={destinatario.celular}
                          onChange={handleDestinatarioChange(index, "celular")}
                          placeholder="No. Celular"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={3}>
                        <SuiInput
                          value={destinatario.email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder="Correo Electrónico"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={3}>
                        <SuiInput
                          value={destinatario.direccion || ""}
                          onChange={handleDestinatarioChange(index, "direccion")}
                          placeholder="Dirección"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </SuiBox>
                ))}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <SuiButton onClick={hCClose} buttonColor="light">
              Cancelar
            </SuiButton>
            <SuiButton
              type="submit"
              variant="gradient"
              buttonColor="dark"
              disabled={saving || processingImages || uploadingImage}
            >
              {saving || processingImages ? "Procesando..." : "Crear"}
            </SuiButton>
          </DialogActions>
        </form>
      </Dialog>

      <SelectProceso
        open={showProcesoModal}
        onClose={() => setShowProcesoModal(false)}
        onSelect={handleProcesoSelect}
      />
    </>
  );
};

CreateComunicacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CreateComunicacion;
